import type * as Y from 'yjs';
import type { Node } from '../models/scene';

/** Presentation only: never put hydrated records, labels or relationships here. */
export const PROJECTED_LAYOUT_MAP = 'projectedLayout';
export const PROJECTED_POSITIONS_MAP = 'projectedNodePositions';
/** Presentation-only overrides for source-backed org cards. */
export const ORG_CUSTOM_FIELD_OVERRIDES_MAP = 'orgCustomFieldOverrides';
export type ProjectedLayoutMode = 'automatic' | 'custom';
export type ProjectedPosition = { x: number; y: number, attrs?: any };
export type ProjectedLayout = {
  mode: ProjectedLayoutMode;
  positions: ReadonlyMap<string, ProjectedPosition>;
};

export function readProjectedPosition(value: unknown): ProjectedPosition | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)
    ? { x, y } : undefined;
}

export function readProjectedLayout(doc: Y.Doc): ProjectedLayout | undefined {
  const mode = doc.getMap(PROJECTED_LAYOUT_MAP).get('mode');
  if (mode !== 'automatic' && mode !== 'custom') return undefined;
  const positions = new Map<string, ProjectedPosition>();
  doc.getMap(PROJECTED_POSITIONS_MAP).forEach((value, id) => {
    const position = readProjectedPosition(value);
    if (position) positions.set(id, position);
  });
  return { mode, positions };
}

/** Only callers holding a command transaction may write presentation. */
export function writeProjectedPosition(doc: Y.Doc, id: string, value: ProjectedPosition): void {
  const position = readProjectedPosition(value);
  if (!position) throw new Error('Projected placement must have finite coordinates');
  // Each card is one atomic CRDT value; moving one never overwrites another.
  doc.getMap(PROJECTED_POSITIONS_MAP).set(id, position);
}

/** New source cards go in a stable, non-overlapping column beside the saved layout. */
export function resolveProjectedPositions(nodes: readonly Node[], layout: ProjectedLayout): Map<string, ProjectedPosition> {
  const result = new Map<string, ProjectedPosition>();
  let right = -Infinity;
  let top = Infinity;
  for (const node of nodes) {
    const saved = layout.positions.get(node.id);
    if (!saved) continue;
    result.set(node.id, saved);
    right = Math.max(right, saved.x + (node.w ?? node.width ?? 240));
    top = Math.min(top, saved.y);
  }
  // A first capture supplies all positions. Empty/legacy snapshots can still render safely.
  if (!Number.isFinite(right)) {
    for (const node of nodes) {
      const position = readProjectedPosition(node);
      if (position) result.set(node.id, position);
    }
    return result;
  }
  let nextY = top;
  for (const node of [...nodes].sort((a, b) => a.id.localeCompare(b.id))) {
    if (result.has(node.id)) continue;
    result.set(node.id, { x: right + 100, y: nextY });
    nextY += (node.h ?? node.height ?? 120) + 60;
  }
  return result;
}
